'use client'
import { useState, useEffect } from 'react'
import classNames from 'classnames'
import { useActiveNav } from '../context/ActiveNavContext'

// ArrowRight paints its stroke from a prop, so the highlight colors are passed
// as values instead of classes. Kept in sync with blue-base / blue-accent.
const CURRENT_PAGE_COLOR = '#041B93'
const SELECTED_COLOR = '#154199'

const ArrowRight = ({ size = 16, color = '#1C274C', strokeWidth = 2 }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M4 12H20M20 12L14 6M20 12L14 18"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

const ArrowLeft = ({ size = 16, color = '#1C274C', strokeWidth = 2 }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M5 12H19M5 12L11 6M5 12L11 18"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

// CTA to the landing page of the section currently shown in the panel
const ViewAllButton = ({ item }) => {
  if (!item?.viewAllLabel || !item?.link) return null

  return (
    <a
      href={item.link}
      className="inline-block w-fit rounded-md bg-blue-base px-4 py-2 text-white no-underline"
      track={{
        click_id: item.label,
        click_text: item.label,
        click_url: item.link,
        nav_link_section: 'Mega Menu',
        track_event: 'mega_menu',
        event_action: 'link_click',
        event_type: 'link_click',
      }}
    >
      <p className="labelMd">{item.viewAllLabel}</p>
    </a>
  )
}

const SubPanelItemsComponent = ({ list }) => {
  const { isActive } = useActiveNav()
  const items = []

  for (const [index, item] of list.entries()) {
    if (item.variant === 'articleCard') {
      const { cardTitleText, cardText, cardImage } = item.content

      const LinkToArticle = ({ children, className }) => {
        return (
          <a
            href={item.link}
            aria-current={isActive(item) ? 'page' : undefined}
            className={classNames('cursor-pointer hover:underline', className)}
            track={{
              click_id: item.label,
              click_text: item.label,
              click_url: item.link,
              nav_link_section: 'Mega Menu',
              track_event: 'mega_menu',
              event_action: 'link_click',
              event_type: 'link_click',
            }}
          >
            {children}
          </a>
        )
      }

      items.push(
        <div key={`${item.id}-${index}`} size={{ xs: 12, sm: 6, md: 4, lg: 4 }}>
          <LinkToArticle>
            <div className="flex flex-col gap-3">
              <div className="h-40 overflow-hidden">
                <img
                  src={cardImage}
                  alt={cardTitleText}
                  className="size-full rounded-md object-cover"
                />
              </div>
              <p
                className={classNames('labelMd font-bold!', {
                  'text-blue-base': isActive(item),
                  'text-gray-600': !isActive(item),
                })}
              >
                {cardTitleText}
              </p>
              <div>
                <p className="bodySm text-gray-500">
                  {cardText?.length > 50
                    ? `${cardText.substring(0, 50)}...`
                    : cardText}
                </p>
              </div>
            </div>
          </LinkToArticle>
        </div>
      )
    }
  }

  return <div className="flex w-full flex-row gap-3">{items}</div>
}

const TwoPanelNavigation = ({ data }) => {
  const { isActive, isActiveBranch } = useActiveNav()
  // selectedFirstLevel: the item whose children are shown in the right panel
  const [selectedFirstLevel, setSelectedFirstLevel] = useState(null)
  // leftPanelList: the list to show in the left panel (parent level of selectedFirstLevel)
  const [leftPanelList, setLeftPanelList] = useState(null)
  const [navigationHistory, setNavigationHistory] = useState([])

  // Flatten panelItemGroup for first level display
  const flattenFirstLevel = (items) => {
    const flattened = []
    for (const item of items) {
      if (
        item.variant === 'panelItemGroup' ||
        (!item.label && item.children && item.children.length > 0)
      ) {
        flattened.push(...flattenFirstLevel(item.children))
      } else if (item.label) {
        flattened.push(item)
      }
    }

    return flattened
  }

  // Keep second-level sections separated by parent group.
  const flattenSectionItems = (items) => {
    const flattened = []
    for (const item of items || []) {
      if (
        item.variant === 'panelItemGroup' ||
        (!item.label && item.children && item.children.length > 0)
      ) {
        flattened.push(...flattenSectionItems(item.children))
      } else if (item.label) {
        flattened.push(item)
      }
    }

    return flattened
  }

  const getSecondLevelSections = (items) => {
    const sections = []

    for (const [index, item] of (items || []).entries()) {
      if (!item) continue

      const isGroup =
        item.variant === 'panelItemGroup' ||
        (!item.label && item.children && item.children.length > 0)

      if (isGroup) {
        const sectionItems = flattenSectionItems(item.children || [])
        if (sectionItems.length > 0) {
          sections.push({
            id: item.id || `section-${index}`,
            label: item.label || null,
            items: sectionItems,
          })
        }
      } else if (item.label) {
        sections.push({
          id: item.id || `section-${index}`,
          label: null,
          items: [item],
        })
      }
    }

    return sections
  }

  // Find original item by id
  const findOriginalItem = (itemId, items) => {
    for (const item of items) {
      if (item.id === itemId) {
        return item
      }

      if (item.children && item.children.length > 0) {
        const found = findOriginalItem(itemId, item.children)
        if (found) return found
      }
    }

    return null
  }

  // Handle click on first level (left panel)
  const handleFirstLevelClick = (item) => {
    // Find the item in the current leftPanelList or root
    const searchList = leftPanelList || data.children
    const originalItem = findOriginalItem(item.id, searchList) || item
    if (originalItem.children && originalItem.children.length > 0) {
      setSelectedFirstLevel(originalItem)
    }
  }

  // Handle click on second level (right panel)
  const handleSecondLevelClick = (item) => {
    const originalItem =
      findOriginalItem(item.id, selectedFirstLevel?.children || []) || item
    if (originalItem.children && originalItem.children.length > 0) {
      // Save current state to history
      setNavigationHistory((prev) => [
        ...prev,
        {
          leftPanelList: leftPanelList,
          selectedFirstLevel: selectedFirstLevel,
        },
      ])
      // The clicked item's parent level becomes the new left panel
      // The clicked item becomes the new selectedFirstLevel (its children go to right)
      setLeftPanelList(selectedFirstLevel?.children || [])
      setSelectedFirstLevel(originalItem)
    }
  }

  // Handle back button
  const handleBack = () => {
    if (navigationHistory.length > 0) {
      const previousState = navigationHistory[navigationHistory.length - 1]
      setNavigationHistory((prev) => prev.slice(0, -1))
      // Restore previous state
      setLeftPanelList(previousState.leftPanelList)
      setSelectedFirstLevel(previousState.selectedFirstLevel)
    }
  }

  // Initialize selected first level if not set. The branch holding the current
  // page wins so the panel opens on the section the user is already browsing.
  useEffect(() => {
    if (!selectedFirstLevel && data.children && data.children.length > 0) {
      const candidates = flattenFirstLevel(data.children)
        .map((item) => findOriginalItem(item.id, data.children) || item)
        .filter((item) => item.children?.length > 0)

      const initialItem =
        candidates.find((item) => isActiveBranch(item)) || candidates[0]
      if (initialItem) {
        setSelectedFirstLevel(initialItem)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.children])

  // Left panel shows: leftPanelList if set, otherwise data.children (root level)
  const firstLevelList = leftPanelList
    ? flattenFirstLevel(leftPanelList)
    : flattenFirstLevel(data.children)
  const secondLevelSections = selectedFirstLevel
    ? getSecondLevelSections(selectedFirstLevel.children || [])
    : []
  const secondLevelColumns =
    Number(selectedFirstLevel?.columns) > 0
      ? Number(selectedFirstLevel?.columns)
      : 1

  return (
    <div
      data-testid="mega-menu-subnav-panel-list-section"
      className="w-250 flex flex-row gap-2"
    >
      <div className="flex w-[350px] flex-col gap-2 border-0 border-r border-solid border-gray-270 bg-[#F5F9FF] p-8">
        {navigationHistory.length > 0 && (
          <a
            noLink
            onClick={handleBack}
            className="mb-2 cursor-pointer text-black-base"
          >
            <div className={classNames('flex flex-row items-center gap-2')}>
              <ArrowLeft />
              <p className="font-medium text-black-base">Back</p>
            </div>
          </a>
        )}
        {firstLevelList.map((item, index) => {
          const hasChildren =
            Array.isArray(item.children) && item.children.length > 0
          const shouldUseLink = !hasChildren && !!item.link
          // Check if item is selected - need to search in the correct list
          const searchList = leftPanelList || data.children
          const originalItem = findOriginalItem(item.id, searchList) || item
          const isSelected = selectedFirstLevel?.id === originalItem.id
          // The entry that holds the page the user is on
          const isCurrent = isActiveBranch(originalItem)

          return (
            <div
              key={`${item.id || index}-${index}`}
              className={classNames(
                'flex flex-col gap-2 border-0 border-b border-solid border-gray-270 py-2',
                {
                  'font-bold! text-blue-700': isSelected,
                  'last:border-b-0': index === firstLevelList.length - 1,
                }
              )}
            >
              {hasChildren ? (
                <div
                  onClick={() => handleFirstLevelClick(item)}
                  className={classNames(
                    'font-medium! cursor-pointer text-black-base',
                    {
                      'font-bold! text-blue-base!': isCurrent,
                      'font-bold! text-blue-585!': isSelected && !isCurrent,
                    }
                  )}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      handleFirstLevelClick(item)
                    }
                  }}
                >
                  <div
                    className={classNames('flex flex-row items-center gap-2')}
                  >
                    <p className="labelMd whitespace-nowrap">{item.label}</p>
                    <ArrowRight
                      color={
                        isCurrent
                          ? CURRENT_PAGE_COLOR
                          : isSelected
                          ? SELECTED_COLOR
                          : undefined
                      }
                    />
                  </div>
                </div>
              ) : shouldUseLink ? (
                <a
                  href={item.link}
                  aria-current={isCurrent ? 'page' : undefined}
                  className="text-black-base"
                  track={{
                    click_id: item.label,
                    click_text: item.label,
                    click_url: item.link,
                    nav_link_section: 'Mega Menu',
                    track_event: 'mega_menu',
                    event_action: 'link_click',
                    event_type: 'link_click',
                  }}
                >
                  <div
                    className={classNames('flex flex-row items-center gap-2')}
                  >
                    <p
                      className={classNames('labelMd whitespace-nowrap', {
                        'font-bold! text-blue-base': isCurrent,
                      })}
                    >
                      {item.label}
                    </p>
                  </div>
                </a>
              ) : (
                <div className={classNames('flex flex-row items-center gap-2')}>
                  <p
                    className={classNames('labelMd whitespace-nowrap', {
                      'font-bold! text-blue-base': isCurrent,
                    })}
                  >
                    {item.label}
                  </p>
                </div>
              )}
            </div>
          )
        })}
      </div>
      <div className="flex w-full flex-col gap-6 px-4 py-8">
        {selectedFirstLevel && (
          <>
            <p className="labelMd font-bold! text-gray-500">
              {selectedFirstLevel.label}
            </p>
            <div
              className="grid gap-3"
              style={{
                gridTemplateColumns: `repeat(${secondLevelColumns}, minmax(0, 1fr))`,
              }}
            >
              {secondLevelSections.map((section, sectionIndex) => (
                <div
                  key={`${section.id}-${sectionIndex}`}
                  className="flex flex-col gap-2"
                >
                  {section.label && (
                    <p className="labelSm font-bold! text-gray-500">
                      {section.label}
                    </p>
                  )}
                  {section.items.map((item, itemIndex) => {
                    const hasChildren =
                      Array.isArray(item.children) && item.children.length > 0
                    const shouldUseLink = !hasChildren && !!item.link
                    const shouldNavigate = hasChildren
                    const isCurrent = isActiveBranch(item)
                    const labelClassName = classNames('labelMd', {
                      'font-bold! text-blue-base': isCurrent,
                    })

                    const handleClick = (e) => {
                      if (shouldNavigate) {
                        e.preventDefault()
                        e.stopPropagation()
                        handleSecondLevelClick(item)
                      }
                    }

                    return (
                      <div
                        key={`${item.id || itemIndex}-${itemIndex}`}
                        className={classNames(
                          'hover:underline-none flex flex-col gap-2 rounded-md px-4 py-2 hover:bg-[#F5F9FF]',
                          { 'bg-blue-135': isCurrent }
                        )}
                      >
                        {shouldNavigate ? (
                          <div
                            onClick={handleClick}
                            className="font-medium! cursor-pointer text-black-base"
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                handleSecondLevelClick(item)
                              }
                            }}
                          >
                            <div
                              className={classNames(
                                'flex flex-row items-center gap-2'
                              )}
                            >
                              <p className={labelClassName}>{item.label}</p>
                              <ArrowRight
                                color={
                                  isCurrent ? CURRENT_PAGE_COLOR : undefined
                                }
                              />
                            </div>
                          </div>
                        ) : shouldUseLink ? (
                          <a
                            href={item.link}
                            aria-current={isActive(item) ? 'page' : undefined}
                            className="text-black-base"
                          >
                            <div
                              className={classNames(
                                'flex flex-row items-center gap-2'
                              )}
                            >
                              <p
                                className={classNames(
                                  labelClassName,
                                  'font-medium'
                                )}
                              >
                                {item.label}
                              </p>
                            </div>
                          </a>
                        ) : (
                          <div
                            className={classNames(
                              'flex flex-row items-center gap-2'
                            )}
                          >
                            <p className={labelClassName}>{item.label}</p>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              ))}
            </div>
            <ViewAllButton item={selectedFirstLevel} />
          </>
        )}
      </div>
    </div>
  )
}

const RecursivePanel = ({ list, level = 1 }) => {
  const { isActive, isActiveBranch } = useActiveNav()
  const [currentList, setCurrentList] = useState(list || [])
  // currentParent: the item whose children are being listed, holds the view all link
  const [currentParent, setCurrentParent] = useState(null)
  const [navigationHistory, setNavigationHistory] = useState([])

  // Sync currentList when list prop changes
  useEffect(() => {
    if (list) {
      setCurrentList(list)
      setNavigationHistory([])
      setCurrentParent(null)
    }
  }, [list])

  // Calculate current level based on navigation history
  // level starts at 1, and each navigation adds 1
  const currentLevel = level + navigationHistory.length

  // Flatten the list to handle panelItemGroup and other intermediate levels
  // This only flattens for display, but we need to find the original item for navigation
  const flattenList = (items) => {
    const flattened = []
    for (const item of items) {
      // If it's a panelItemGroup (or similar container without label), use its children directly
      if (
        item.variant === 'panelItemGroup' ||
        (!item.label && item.children && item.children.length > 0)
      ) {
        // Recursively flatten the children, but keep reference to original item
        const childrenItems = flattenList(item.children)
        flattened.push(...childrenItems)
      } else if (item.label) {
        // Only include items that have a label
        flattened.push(item)
      }
    }

    return flattened
  }

  // Find the original item in the currentList (before flattening) by id
  const findOriginalItem = (flattenedItem) => {
    const findInList = (items) => {
      for (const item of items) {
        if (item.id === flattenedItem.id) {
          return item
        }

        if (item.children && item.children.length > 0) {
          const found = findInList(item.children)
          if (found) return found
        }
      }

      return null
    }

    return findInList(currentList)
  }

  const handleItemClick = (item) => {
    // Find the original item to get the correct children structure
    const originalItem = findOriginalItem(item) || item
    if (originalItem.children && originalItem.children.length > 0) {
      // Navigate to children level
      setNavigationHistory((prev) => [
        ...prev,
        { list: currentList, parent: currentParent },
      ])
      setCurrentList(originalItem.children)
      setCurrentParent(originalItem)
    }
  }

  const handleBack = () => {
    if (navigationHistory.length > 0) {
      const previousState = navigationHistory[navigationHistory.length - 1]
      setNavigationHistory((prev) => prev.slice(0, -1))
      setCurrentList(previousState.list)
      setCurrentParent(previousState.parent)
    }
  }

  const showBackButton = navigationHistory.length > 0 || level > 1

  // Get the flattened list for display
  const displayList = flattenList(currentList)

  return (
    <div
      data-testid="mega-menu-subnav-panel-section"
      className="flex w-screen flex-col gap-4 bg-[#F5F9FF] px-10 py-6"
    >
      {showBackButton && (
        <a noLink onClick={handleBack} className="text-black-base">
          <div className={classNames('mb-4 flex flex-row items-center gap-2')}>
            <ArrowLeft />
            <p className="labelMd">Back</p>
          </div>
        </a>
      )}
      {displayList.map((child, index) => {
        const hasChildren =
          Array.isArray(child.children) && child.children.length > 0
        // Only use link if there are NO children
        const shouldUseLink = !hasChildren && !!child.link
        const shouldNavigate = hasChildren
        const isCurrent = isActiveBranch(child)
        const labelClassName = classNames('labelMd', {
          'font-bold! text-blue-base': isCurrent,
        })

        const handleClick = (e) => {
          if (shouldNavigate) {
            e.preventDefault()
            e.stopPropagation()
            handleItemClick(child)
          }
        }

        return (
          <div
            key={`${child.id || index}-${index}`}
            className={classNames('flex flex-col gap-2 pb-2', {
              'border-0 border-b-[0.5px] border-solid border-gray-270 pb-4':
                currentLevel === 1,
              'last:border-b-0':
                currentLevel === 1 && index === displayList.length - 1,
            })}
          >
            {shouldNavigate ? (
              <div
                onClick={handleClick}
                className="cursor-pointer font-medium text-black-base"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    handleItemClick(child)
                  }
                }}
              >
                <div className={classNames('flex flex-row items-center gap-2')}>
                  <p className={labelClassName}>{child.label}</p>
                  <ArrowRight
                    color={isCurrent ? CURRENT_PAGE_COLOR : undefined}
                  />
                </div>
              </div>
            ) : shouldUseLink ? (
              <a
                href={child.link}
                key={`${child.id}`}
                aria-current={isActive(child) ? 'page' : undefined}
                className="font-medium text-black-base"
                track={{
                  click_id: child.label,
                  click_text: child.label,
                  click_url: child.link,
                  nav_link_section: 'Mega Menu',
                  track_event: 'mega_menu',
                  event_action: 'link_click',
                  event_type: 'link_click',
                }}
              >
                <div className={classNames('flex flex-row items-center gap-2')}>
                  <p className={labelClassName}>{child.label}</p>
                </div>
              </a>
            ) : (
              <div
                className={classNames(
                  'flex flex-row items-center gap-2 font-medium text-black-base'
                )}
              >
                <p className={labelClassName}>{child.label}</p>
              </div>
            )}
          </div>
        )
      })}
      <ViewAllButton item={currentParent} />
    </div>
  )
}

export const SubnavPanelSection = ({ isMobile, data, setHoverSubnav }) => {
  const { isActive, isActiveBranch } = useActiveNav()
  const variant = data.variant
  const [subPanel, setSubPanel] = useState(null)

  useEffect(() => {
    if (variant === 'panelList' && !isMobile && subPanel === null) {
      // Open on the branch holding the current page, first entry otherwise
      const parent =
        data.children?.find((child) => isActiveBranch(child)) ??
        data.children?.[0]
      const panel =
        parent?.children?.find((child) => isActiveBranch(child)) ??
        parent?.children?.[0]
      setSubPanel(panel)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, variant, subPanel, isMobile])

  if (variant === 'panelList') {
    if (isMobile) {
      return (
        <div
          data-testid="mega-menu-subnav-default-section-mobile"
          className="flex w-screen flex-col gap-4 bg-[#F5F9FF] px-10 py-6 lg:-ml-primary-xs"
        >
          {data?.children?.map((child, index) => (
            <div
              key={`${data.id}-${child.id}`}
              className={classNames(
                'flex flex-col gap-2 border-0 border-b-[0.5px] border-solid border-gray-270 pb-4 font-medium',
                {
                  'last:border-b-0': index === data.children.length - 1,
                }
              )}
            >
              <a
                href={child.link}
                key={`${data.id}-${child.id}-${child.id}`}
                aria-current={isActive(child) ? 'page' : undefined}
                className="text-black-base"
                track={{
                  click_id: child.label,
                  click_text: child.label,
                  click_url: child.link,
                  nav_link_section: 'Mega Menu',
                  track_event: 'mega_menu',
                  event_action: 'link_click',
                  event_type: 'link_click',
                }}
              >
                <div className={classNames('flex flex-row items-center gap-2')}>
                  <p
                    className={classNames('labelMd', {
                      'font-bold! text-blue-base': isActiveBranch(child),
                    })}
                  >
                    {child.label}
                  </p>
                </div>
              </a>
            </div>
          ))}
        </div>
      )
    } else {
      return (
        <div
          data-testid="mega-menu-subnav-panel-list-section"
          className="w-250 flex flex-row gap-2"
        >
          <div className="flex w-[350px] flex-col gap-2 border-0 border-r border-solid border-gray-270 bg-[#F5F9FF] p-10">
            {data?.children?.map((child, index) => (
              <div
                key={`${data.id}-${child.id}`}
                className={classNames(
                  'flex flex-col gap-2 border-0 border-b border-solid border-gray-270 py-2',
                  {
                    'last:border-b-0': index === data.children.length - 1,
                  }
                )}
              >
                <a
                  noLink={!!child.children?.[0]}
                  href={child.link}
                  key={`${data.id}-${child.id}-${child.id}`}
                  aria-current={isActive(child) ? 'page' : undefined}
                  className="cursor-pointer font-medium text-black-base hover:underline"
                  onClick={() => {
                    if (!child.children?.[0]) {
                      setHoverSubnav(false)

                      return
                    }

                    setSubPanel(child.children?.[0])
                  }}
                >
                  <div
                    className={classNames('flex flex-row items-center gap-2')}
                  >
                    <p
                      className={classNames('labelMd whitespace-nowrap', {
                        'font-bold! text-blue-base!': isActiveBranch(child),
                        'font-bold! text-blue-585!':
                          !isActiveBranch(child) &&
                          !!subPanel?.id &&
                          subPanel.id === child.children?.[0]?.id,
                      })}
                    >
                      {child.label}
                    </p>
                    {child.children?.[0] && (
                      <ArrowRight
                        color={
                          isActiveBranch(child) ? CURRENT_PAGE_COLOR : undefined
                        }
                      />
                    )}
                  </div>
                </a>
              </div>
            ))}
          </div>
          {subPanel && (
            <div className="flex flex-col gap-6 px-4 py-8">
              <p className="labelMd font-bold! text-gray-500">
                {subPanel.label}
              </p>
              <SubPanelItemsComponent list={subPanel.children} />
              <ViewAllButton key={`${subPanel.id}-view-all`} item={subPanel} />
            </div>
          )}
        </div>
      )
    }
  } else if (variant === 'panel') {
    if (isMobile) {
      return <RecursivePanel list={data.children} />
    } else {
      return <TwoPanelNavigation data={data} />
    }
  }
}

export default SubnavPanelSection
